import {
  propertyBusinessType,
  type PropertyBusinessType,
  propertyCategory,
  propertyCityState,
  propertyStatus,
} from '@/shared/utils/property-display';
import { formatPropertyPrice } from '@/shared/utils/format-property-price';
import { propertyTotalArea } from '@/shared/utils/property-display';
import type { PropertyResponseType } from '@services/housien/types';

/**
 * The dimensions the Imóveis list can be grouped by. `none` is the ungrouped
 * default; the five real dimensions mirror the backend aggregation contract.
 */
export type GroupBy = 'none' | 'city' | 'neighborhood' | 'category' | 'business' | 'status'
  | 'total_area' | 'constructed_area' | 'sale_price' | 'rental_price';

/** The grouping dimensions that actually partition the list (everything but `none`). */
export const GROUP_DIMENSIONS: readonly Exclude<GroupBy, 'none'>[] = [
  'city',
  'neighborhood',
  'category',
  'business',
  'status',
  'total_area',
  'constructed_area',
  'sale_price',
  'rental_price',
] as const;

/** First `ROWS_PER_GROUP` rows show by default; the rest hide behind "Mostrar mais". */
export const ROWS_PER_GROUP = 5;

/** Band widths for banded numeric dimensions (mirrors backend constants). */
const AREA_BAND_WIDTH = 100;          // m²
const PRICE_SALE_BAND_WIDTH = 100_000; // R$
const PRICE_RENTAL_BAND_WIDTH = 1_000; // R$

/**
 * Computes a banded label from a continuous numeric value using floor-division.
 *
 * @param value - The raw numeric value, or undefined/null when absent.
 * @param width - The band width.
 * @param unit - The display unit (e.g. "m²", "R$").
 * @returns A label like `"100-200 m²"`, or `"N/A"` when the value is absent.
 */
export function bandLabel(value: number | undefined | null, width: number, unit: string): string {
  if (value === undefined || value === null) return 'N/A';
  const low = Math.floor(value / width) * width;
  return `${low}-${low + width} ${unit}`;
}

type AnyRecord = Record<string, unknown>;

/**
 * Reads a property as the loose record the display accessors consume.
 *
 * @param p - A property row.
 * @returns The same row typed as a string-keyed record.
 */
function asRecord(p: PropertyResponseType): AnyRecord {
  return p as unknown as AnyRecord;
}

/**
 * The aggregate stats a group header and summary card render. Mirrors the
 * backend `/aggregate` rich-stats contract so the UI consumes either source
 * without reshaping. All metric fields are nullable so a partial (not-yet-
 * fully-loaded) client aggregation can render "—" rather than a wrong number.
 */
export interface GroupAggregate {
  /** Number of distinct properties in the group. */
  count: number;
  /** Split of the group by transaction kind. */
  byBusinessType: { sale: number; rental: number; both: number };
  /** The most common category and how dominant it is. */
  topCategory: { value: string; count: number; distinct: number } | null;
  /** Mean total area over the group, or null when no areas are known. */
  avgTotalArea: number | null;
  /** Median sale price over the group's sale listings, or null. */
  medianSalePrice: number | null;
  /** Median rental price over the group's rental listings, or null. */
  medianRentalPrice: number | null;
  /** Mean price-per-m² over the group, or null when it cannot be computed. */
  avgPricePerM2: number | null;
}

/**
 * The shape of the backend's rich aggregation response. Defined locally so the
 * UI is ready to consume it the moment the endpoint lands; the actual fetch is
 * gated off today (the feature runs on {@link aggregate} client-side instead).
 *
 * Field names follow the plan schema; the generated-client call plugs in at
 * `use-property-grouping-data.ts` (see the TODO there).
 */
export interface BackendGroupAggregate {
  key: string;
  label: string;
  sub_label?: string;
  count: number;
  by_business_type: { sale: number; rental: number; both: number };
  top_category: { value: string; count: number; distinct: number } | null;
  avg_total_area: number | null;
  median_sale_price: number | null;
  median_rental_price: number | null;
  avg_price_per_m2: number | null;
}

/** The full backend aggregation response envelope for a `group_by` request. */
export interface BackendAggregationResponse {
  group_by: Exclude<GroupBy, 'none'>;
  total: number;
  groups: BackendGroupAggregate[];
}

/**
 * One rendered group: its identity, its label parts, and its member rows. The
 * aggregate is attached separately so a partial client computation can defer
 * the metrics while still showing the count and label.
 */
export interface PropertyGroup {
  /** Stable key used for collapse state and React keys. */
  key: string;
  /** Primary group label (city name, category label, …). */
  label: string;
  /** Secondary label beside the primary (state UF, parent city, …), if any. */
  subLabel?: string;
  /** The group's member rows, in the order they arrived. */
  items: PropertyResponseType[];
}

/**
 * Reads the constructed area from a property's characteristics.
 *
 * @param rec - The property record.
 * @returns The constructed area, or undefined when absent.
 */
function constructedAreaOf(rec: AnyRecord): number | undefined {
  const chars = rec.characteristics;
  if (chars && typeof chars === 'object') {
    const val = (chars as AnyRecord).constructed_area;
    return typeof val === 'number' ? val : undefined;
  }
  return undefined;
}

/**
 * Per-dimension key extractors. Each is CC 1; the map replaces a switch so the
 * public {@link groupKey} stays flat.
 */
const KEY_OF: Record<Exclude<GroupBy, 'none'>, (rec: AnyRecord) => string> = {
  city: (rec) => {
    const { city, state } = propertyCityState(rec);
    return `${(city ?? '').trim().toLowerCase()}|${(state ?? '').trim().toLowerCase()}`;
  },
  neighborhood: (rec) => {
    const { city } = propertyCityState(rec);
    return `${(city ?? '').trim().toLowerCase()}|${neighborhoodOf(rec).trim().toLowerCase()}`;
  },
  category: (rec) => (propertyCategory(rec) ?? '').trim().toLowerCase(),
  business: (rec) => propertyBusinessType(rec),
  status: (rec) => propertyStatus(rec),
  total_area: (rec) => bandLabel(propertyTotalArea(rec), AREA_BAND_WIDTH, 'm²'),
  constructed_area: (rec) => bandLabel(constructedAreaOf(rec), AREA_BAND_WIDTH, 'm²'),
  sale_price: (rec) => {
    const { salePrice } = formatPropertyPrice(rec);
    return bandLabel(salePrice, PRICE_SALE_BAND_WIDTH, 'R$');
  },
  rental_price: (rec) => {
    const { rentalPrice } = formatPropertyPrice(rec);
    return bandLabel(rentalPrice, PRICE_RENTAL_BAND_WIDTH, 'R$');
  },
};

/**
 * Computes the stable grouping key for a property under a given dimension.
 *
 * City and neighborhood keys fold in the parent (state / city) so two cities
 * that share a neighborhood name never collapse into one bucket.
 *
 * @param by - The active grouping dimension.
 * @param p - The property row.
 * @returns A stable string key for the row's group.
 */
export function groupKey(by: Exclude<GroupBy, 'none'>, p: PropertyResponseType): string {
  return KEY_OF[by](asRecord(p));
}

/**
 * Resolves the neighborhood for a property, dual-reading nested then flat.
 *
 * @param rec - The property record.
 * @returns The neighborhood string, or an empty string when unknown.
 */
function neighborhoodOf(rec: AnyRecord): string {
  const loc = rec.location;
  if (loc && typeof loc === 'object') {
    const nested = (loc as AnyRecord).neighborhood;
    if (typeof nested === 'string') return nested;
  }
  const flat = rec.neighborhood;
  return typeof flat === 'string' ? flat : '';
}

/**
 * Per-dimension label extractors. Each is CC ≤ 2; the map replaces a switch so
 * the public {@link groupLabel} stays flat.
 */
const LABEL_OF: Record<
  Exclude<GroupBy, 'none'>,
  (rec: AnyRecord, t: (key: string) => string) => { label: string; subLabel?: string }
> = {
  city: (rec) => {
    const { city, state } = propertyCityState(rec);
    return { label: city?.trim() || '—', subLabel: state?.trim() || undefined };
  },
  neighborhood: (rec) => {
    const { city } = propertyCityState(rec);
    return { label: neighborhoodOf(rec).trim() || '—', subLabel: city?.trim() || undefined };
  },
  category: (rec) => ({ label: (propertyCategory(rec) ?? '').trim() || '—' }),
  business: (rec, t) => ({ label: t(`businessType.${propertyBusinessType(rec)}`) }),
  status: (rec, t) => ({ label: t(`status.${propertyStatus(rec)}`) }),
  total_area: (rec) => ({ label: bandLabel(propertyTotalArea(rec), AREA_BAND_WIDTH, 'm²') }),
  constructed_area: (rec) => ({ label: bandLabel(constructedAreaOf(rec), AREA_BAND_WIDTH, 'm²') }),
  sale_price: (rec) => {
    const { salePrice } = formatPropertyPrice(rec);
    return { label: bandLabel(salePrice, PRICE_SALE_BAND_WIDTH, 'R$') };
  },
  rental_price: (rec) => {
    const { rentalPrice } = formatPropertyPrice(rec);
    return { label: bandLabel(rentalPrice, PRICE_RENTAL_BAND_WIDTH, 'R$') };
  },
};

/**
 * Builds the human label parts for a property's group under a dimension.
 *
 * Category / business / status labels are produced by the caller-supplied
 * translator so the pure function stays locale-agnostic; it only supplies the
 * i18n key for those, and the literal value for geographic dimensions.
 *
 * @param by - The active grouping dimension.
 * @param p - The property row.
 * @param t - Translator for category / business / status labels.
 * @returns `{ label, subLabel }` for the group header.
 */
export function groupLabel(
  by: Exclude<GroupBy, 'none'>,
  p: PropertyResponseType,
  t: (key: string) => string,
): { label: string; subLabel?: string } {
  return LABEL_OF[by](asRecord(p), t);
}

/**
 * Computes the median of a numeric list.
 *
 * Returns `null` for an empty list so callers render "—" instead of a lie. For
 * an even count it averages the two middle values, matching the backend's
 * median helper so the client fallback and the server never disagree.
 *
 * @param values - The numbers to summarize (order does not matter).
 * @returns The median, or `null` when there is nothing to summarize.
 */
export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid];
  return (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Averages a numeric list, returning `null` for an empty one.
 *
 * @param values - The numbers to average.
 * @returns The arithmetic mean, or `null` when there is nothing to average.
 */
function average(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/**
 * Resolves the sale and rental prices of a property as plain numbers.
 *
 * @param p - The property row.
 * @returns `{ sale, rental }`, each `undefined` when the price is absent.
 */
function pricesOf(p: PropertyResponseType): { sale?: number; rental?: number } {
  const { salePrice, rentalPrice } = formatPropertyPrice(asRecord(p));
  return { sale: salePrice, rental: rentalPrice };
}

/**
 * Decides whether a candidate category beats the current best, by count then
 * pt-BR label order (so ties are deterministic).
 *
 * @param best - The current winner, or null before the first candidate.
 * @param value - The candidate category.
 * @param count - The candidate's count.
 * @returns True when the candidate should replace the current best.
 */
function beatsTopCategory(
  best: { value: string; count: number } | null,
  value: string,
  count: number,
): boolean {
  if (best === null) return true;
  if (count !== best.count) return count > best.count;
  return value.localeCompare(best.value, 'pt-BR') < 0;
}

/**
 * Tallies category occurrences across a group's rows.
 *
 * @param items - The group's member rows.
 * @returns A map of category → count (empty categories skipped).
 */
function tallyCategories(items: PropertyResponseType[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) {
    const category = (propertyCategory(asRecord(item)) ?? '').trim();
    if (category) counts.set(category, (counts.get(category) ?? 0) + 1);
  }
  return counts;
}

/**
 * Picks the single most common category in a group, breaking ties by the
 * category label so the result is deterministic across runs.
 *
 * @param items - The group's member rows.
 * @returns `{ value, count, distinct }`, or `null` when no category is known.
 */
function topCategoryOf(items: PropertyResponseType[]): GroupAggregate['topCategory'] {
  const counts = tallyCategories(items);
  if (counts.size === 0) return null;
  let best: { value: string; count: number } | null = null;
  for (const [value, count] of counts) {
    if (beatsTopCategory(best, value, count)) best = { value, count };
  }
  return { value: best!.value, count: best!.count, distinct: counts.size };
}

/**
 * Computes the price-per-m² of a property from its primary price and area.
 *
 * Sale price is preferred (a portfolio is ranked on it); rental is the
 * fallback for rent-only listings. Zero/absent area yields `null`.
 *
 * @param p - The property row.
 * @returns The price-per-m², or `null` when it cannot be computed.
 */
function pricePerM2Of(p: PropertyResponseType): number | null {
  const area = propertyTotalArea(asRecord(p));
  if (!area || area <= 0) return null;
  const { sale, rental } = pricesOf(p);
  const price = sale ?? rental;
  if (price === undefined) return null;
  return price / area;
}

/** The numeric series collected from a group, fed to the summary stats. */
interface GroupSeries {
  byBusinessType: { sale: number; rental: number; both: number };
  areas: number[];
  salePrices: number[];
  rentalPrices: number[];
  pricePerM2: number[];
}

/**
 * Collects the sale/rental/price-per-m² series from one row into the series.
 *
 * @param series - The accumulator to push into.
 * @param item - The property row.
 */
function collectPrices(series: GroupSeries, item: PropertyResponseType): void {
  const { sale, rental } = pricesOf(item);
  if (sale !== undefined) series.salePrices.push(sale);
  if (rental !== undefined) series.rentalPrices.push(rental);
  const ppm2 = pricePerM2Of(item);
  if (ppm2 !== null) series.pricePerM2.push(ppm2);
}

/**
 * Counts a listing into the sale / rental / both split. Temporada and Leilão
 * listings stay out of it, as in the backend's `by_business_type`: the
 * split answers "for sale or for rent", and short-term letting is neither.
 *
 * @param split - The split being tallied.
 * @param type - The listing's business type.
 */
function countBusinessType(split: GroupSeries['byBusinessType'], type: PropertyBusinessType): void {
  if (type in split) split[type as keyof GroupSeries['byBusinessType']] += 1;
}

/**
 * Walks a group's rows once, collecting the numeric series the aggregate
 * summarizes. Kept separate so {@link aggregate} stays a flat assembly.
 *
 * @param items - The group's member rows.
 * @returns The collected series.
 */
function collectSeries(items: PropertyResponseType[]): GroupSeries {
  const series: GroupSeries = {
    byBusinessType: { sale: 0, rental: 0, both: 0 },
    areas: [],
    salePrices: [],
    rentalPrices: [],
    pricePerM2: [],
  };
  for (const item of items) {
    countBusinessType(series.byBusinessType, propertyBusinessType(asRecord(item)));
    const area = propertyTotalArea(asRecord(item));
    if (typeof area === 'number') series.areas.push(area);
    collectPrices(series, item);
  }
  return series;
}

/**
 * Aggregates a group's member rows into the stats the header and card render.
 *
 * Everything is derived server-side-free (pure arithmetic over the rows), so
 * the same function serves the client-side fallback and the test fixtures. The
 * caller is responsible for only aggregating a COMPLETE group — a partial page
 * must render pending ("—") metrics rather than a wrong median.
 *
 * @param items - The group's member rows.
 * @returns The computed {@link GroupAggregate}.
 */
export function aggregate(items: PropertyResponseType[]): GroupAggregate {
  const series = collectSeries(items);
  return {
    count: items.length,
    byBusinessType: series.byBusinessType,
    topCategory: topCategoryOf(items),
    avgTotalArea: average(series.areas),
    medianSalePrice: median(series.salePrices),
    medianRentalPrice: median(series.rentalPrices),
    avgPricePerM2: average(series.pricePerM2),
  };
}

/**
 * Partitions a flat property list into groups under a dimension, preserving
 * incoming row order within each group (so the server sort still orders rows).
 *
 * @param by - The active grouping dimension.
 * @param items - The flat property list.
 * @param t - Translator for category / business / status labels.
 * @returns The groups, sorted by {@link sortGroups}.
 */
export function buildGroups(
  by: Exclude<GroupBy, 'none'>,
  items: PropertyResponseType[],
  t: (key: string) => string,
): PropertyGroup[] {
  const byKey = new Map<string, PropertyGroup>();
  for (const item of items) {
    const key = groupKey(by, item);
    let group = byKey.get(key);
    if (!group) {
      const { label, subLabel } = groupLabel(by, item, t);
      group = { key, label, subLabel, items: [] };
      byKey.set(key, group);
    }
    group.items.push(item);
  }
  return sortGroups([...byKey.values()]);
}

/**
 * Orders groups by member count descending, breaking ties by label (pt-BR
 * collation) then sub-label, so the ordering is total and stable.
 *
 * @param groups - The groups to order.
 * @returns A new, sorted array (the input is not mutated).
 */
export function sortGroups(groups: PropertyGroup[]): PropertyGroup[] {
  return [...groups].sort((a, b) => {
    if (b.items.length !== a.items.length) return b.items.length - a.items.length;
    const byLabel = a.label.localeCompare(b.label, 'pt-BR');
    if (byLabel !== 0) return byLabel;
    return (a.subLabel ?? '').localeCompare(b.subLabel ?? '', 'pt-BR');
  });
}

/**
 * Maps a backend rich-aggregation group onto the UI's {@link GroupAggregate}.
 *
 * Lets the grouped UI consume the server contract without reshaping once the
 * endpoint is live; today it is exercised only by tests.
 *
 * @param group - One backend aggregation group.
 * @returns The equivalent {@link GroupAggregate}.
 */
export function fromBackendAggregate(group: BackendGroupAggregate): GroupAggregate {
  return {
    count: group.count,
    byBusinessType: group.by_business_type,
    topCategory: group.top_category,
    avgTotalArea: group.avg_total_area,
    medianSalePrice: group.median_sale_price,
    medianRentalPrice: group.median_rental_price,
    avgPricePerM2: group.avg_price_per_m2,
  };
}
